/**
 * Skeleton loading screens — UI Wave A.
 * Replace `<Loader2 spinning />` blocks with skeleton screens that mimic the
 * eventual content shape. Perceived performance jumps ~40%.
 */
import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";

/** Dashboard top-of-page skeleton (mimics ribbon + 2-3 cards). */
export function DashboardSkeleton() {
  return (
    <div className="space-y-4" data-testid="dashboard-skeleton">
      <Skeleton className="h-10 w-full rounded-xl" />
      <div className="grid grid-cols-2 gap-3">
        <Skeleton className="h-24 rounded-xl" />
        <Skeleton className="h-24 rounded-xl" />
      </div>
      <Skeleton className="h-32 w-full rounded-xl" />
      <Skeleton className="h-40 w-full rounded-xl" />
    </div>
  );
}

/** Card list (Trouble Cards, Friend Updates, Course list). */
export function CardListSkeleton({ rows = 4 }) {
  return (
    <Card className="p-4 border-slate-200" data-testid="card-list-skeleton">
      <Skeleton className="h-4 w-32 mb-3" />
      <div className="space-y-2.5">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="h-8 w-8 rounded-full" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-2 w-2/3" />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

/** Smart Review session screen — question + 4 choices. */
export function ReviewSessionSkeleton() {
  return (
    <div className="space-y-4" data-testid="review-session-skeleton">
      <div className="flex items-center justify-between">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-3 w-12" />
      </div>
      <Skeleton className="h-2 w-full rounded-full" />
      <Card className="p-5 border-slate-200">
        <Skeleton className="h-4 w-3/4 mb-2" />
        <Skeleton className="h-4 w-5/6 mb-1" />
        <Skeleton className="h-4 w-1/2" />
      </Card>
      <div className="space-y-2.5">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-12 w-full rounded-lg" />
        ))}
      </div>
      <Skeleton className="h-11 w-full rounded-lg" />
    </div>
  );
}

/** Mock Exam results — score + per-subject breakdown. */
export function MockResultSkeleton() {
  return (
    <div className="space-y-4" data-testid="mock-result-skeleton">
      <div className="text-center space-y-2">
        <Skeleton className="h-3 w-24 mx-auto" />
        <Skeleton className="h-20 w-32 mx-auto rounded-2xl" />
      </div>
      <Card className="p-4 border-slate-200">
        <Skeleton className="h-4 w-32 mb-3" />
        <div className="space-y-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-2 flex-1 rounded-full" />
              <Skeleton className="h-3 w-10" />
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
